/**
 * Theme Provider
 *
 * Provides white-label theming context for the portal.
 * Task 2.39.2 | Wave 2f Tier 6
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { PORTAL_BRAND } from '../config/branding';

/**
 * Color scale with semantic tokens
 */
interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
  950: string;
}

/**
 * Theme colors configuration
 */
interface ThemeColors {
  primary: ColorScale;
  neutral: ColorScale;
  success: ColorScale;
  warning: ColorScale;
  error: ColorScale;
}

/**
 * Theme branding configuration
 */
interface ThemeBranding {
  logoUrl?: string;
  logoAlt?: string;
  faviconUrl?: string;
  companyName: string;
  tagline?: string;
  supportEmail?: string;
  supportPhone?: string;
  websiteUrl?: string;
}

/**
 * Complete theme configuration
 */
interface Theme {
  id: string;
  name: string;
  version: string;
  colors: ThemeColors;
  typography: {
    fontFamily: {
      sans: string;
      serif?: string;
      mono?: string;
    };
  };
  borderRadius: Record<string, string>;
  branding: ThemeBranding;
  customCss?: string;
}

/**
 * Theme context value
 */
interface ThemeContextValue {
  theme: Theme | null;
  isLoading: boolean;
  error: string | null;
  branding: ThemeBranding;
  setTheme: (theme: Theme) => void;
  resetTheme: () => void;
}

/**
 * Default branding
 */
export const DEFAULT_BRANDING: ThemeBranding = {
  companyName: PORTAL_BRAND.companyName,
  tagline: PORTAL_BRAND.tagline,
  supportEmail: PORTAL_BRAND.supportEmail,
  supportPhone: PORTAL_BRAND.supportPhone,
  websiteUrl: PORTAL_BRAND.websiteUrl,
  logoUrl: PORTAL_BRAND.logoUrl,
  logoAlt: PORTAL_BRAND.companyName,
};

/**
 * Theme context
 */
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Theme provider props
 */
interface ThemeProviderProps {
  children: ReactNode;
  partnerId?: string;
}

/**
 * Apply theme to CSS variables
 */
function applyThemeToDOM(theme: Theme): void {
  const root = document.documentElement;

  // Apply colors
  if (theme.colors) {
    applyColorScale(root, 'primary', theme.colors.primary);
    applyColorScale(root, 'neutral', theme.colors.neutral);
    applyColorScale(root, 'success', theme.colors.success);
    applyColorScale(root, 'warning', theme.colors.warning);
    applyColorScale(root, 'error', theme.colors.error);
  }

  // Apply typography
  if (theme.typography) {
    root.style.setProperty('--font-sans', theme.typography.fontFamily.sans);
    if (theme.typography.fontFamily.mono) {
      root.style.setProperty('--font-mono', theme.typography.fontFamily.mono);
    }
  }

  // Apply border radius
  if (theme.borderRadius) {
    Object.entries(theme.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-${key}`, value);
    });
  }

  // Apply custom CSS
  if (theme.customCss) {
    let styleEl = document.getElementById('theme-custom-css');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'theme-custom-css';
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = theme.customCss;
  }

  // Update favicon if provided
  if (theme.branding.faviconUrl) {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) {
      favicon.href = theme.branding.faviconUrl;
    }
  }

  // Update page title
  document.title = `${theme.branding.companyName} Portal`;
}

/**
 * Apply color scale to CSS variables
 */
function applyColorScale(
  root: HTMLElement,
  name: keyof ThemeColors,
  scale: ThemeColors[keyof ThemeColors]
): void {
  Object.entries(scale).forEach(([shade, value]) => {
    root.style.setProperty(`--color-${name}-${shade}`, value);
  });
}

/**
 * Reset theme to defaults
 */
function resetThemeFromDOM(): void {
  const root = document.documentElement;

  // Remove custom CSS
  const styleEl = document.getElementById('theme-custom-css');
  if (styleEl) {
    styleEl.remove();
  }

  // Reset to default values - in production, would reload default CSS
  root.style.removeProperty('--font-sans');
  root.style.removeProperty('--font-mono');
}

/**
 * Fetch partner theme from API
 */
async function fetchPartnerTheme(partnerId: string): Promise<Theme | null> {
  try {
    // Mock API call - replace with actual API
    const response = await fetch(`/api/themes/${partnerId}`);
    if (!response.ok) {
      if (response.status === 404) {
        return null; // Partner not found, use default
      }
      throw new Error(`Failed to fetch theme: ${response.statusText}`);
    }
    return (await response.json()) as Theme;
  } catch (error) {
    console.error('[ThemeProvider] Failed to fetch partner theme:', error);
    return null;
  }
}

/**
 * Theme Provider Component
 */
export function ThemeProvider({ children, partnerId }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme | null>(null);
  const [isLoading, setIsLoading] = useState(!!partnerId);
  const [error, setError] = useState<string | null>(null);

  // Load partner theme on mount
  useEffect(() => {
    if (!partnerId) {
      setIsLoading(false);
      return;
    }

    const currentPartnerId = partnerId;
    let cancelled = false;

    async function loadTheme() {
      setIsLoading(true);
      setError(null);

      try {
        const partnerTheme = await fetchPartnerTheme(currentPartnerId);
        if (cancelled) return;

        if (partnerTheme) {
          setThemeState(partnerTheme);
          applyThemeToDOM(partnerTheme);
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load theme';
        setError(message);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void loadTheme();

    return () => {
      cancelled = true;
    };
  }, [partnerId]);

  // Set theme and apply to DOM
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    applyThemeToDOM(newTheme);
  };

  // Reset to default theme
  const resetTheme = () => {
    setThemeState(null);
    resetThemeFromDOM();
  };

  // Get current branding
  const branding = theme?.branding ?? DEFAULT_BRANDING;

  const value: ThemeContextValue = {
    theme,
    isLoading,
    error,
    branding,
    setTheme,
    resetTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Use theme hook
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Use branding hook (simplified access to branding)
 */
export function useBranding(): ThemeBranding {
  const { branding } = useTheme();
  return branding;
}

export default ThemeProvider;
